<template>
<div class="chapter">
<div class="inner_page">
<section epub:type="chapter">
<header>
<h1 class="topictitle1" id="ch4"><a href="#/nav#nch4"><span class="section_number">Chapter 2</span></a></h1>
</header>
<div class="date_group">
<p class="location">Albuquerque, New Mexico</p>
<p class="date">Three weeks later</p>
</div>
<p>“Is this, ah, Evelyn McTavish, thirty-five, zero three, seventeen seventy-nine?” The man’s voice was unpleasantly high-pitched.</p>
<p>Tavish checked the caller ID again. Happy Tails Shelter. “Yes, this is Evelyn McTavish, but I don’t know what those numbers
            mean, and I don’t donate to charity over the phone.”
         </p>
<p>“That’s how the registration came back. Your name, them numbers, this phone number, and the address of 965 Westwood Estates.”</p>
<p>Tavish glanced at her watch. “What registration?”</p>
<p>The man gave an exasperated sigh. “Your dog registration. From the microchip.”</p>
<p>“I don’t own a dog. Please don’t call me again.” She disconnected the call.</p>
<p>The phone rang again. Happy Tails Shelter. “Look,” she began.</p>
<p>“No, you look, lady, and I’ll give it to ya straight. Either pay up and get the dog, or we’re gonna kill it.” The man’s voice
            rose even higher. “We don’t kid around. What’s it gonna be?”
         </p>
<p>She made an effort to loosen her white-knuckled grip on her cell. “And I’m telling you for the second time, I don’t have a
            dog! I’ve never had a dog. I’ve never even owned a goldfish—”
         </p>
<p>“Alls I know is the microchip on this mutt is registered to you. The shelter is overcrowded with dumped dogs, and this one
            bites, so we can’t place her in another home. Come and get her or she’s dead.” <i>Click</i>.
         </p>
<p>The cell phone dropped from Tavish’s shaking hand and onto her lap. Her stomach tightened and knotted and the room spun. She
            glanced at her fingernails, several of which were wrapped in Band-Aids. Finding one that was exposed, she bit down on the
            remains of the ragged nail. The sting made her eyes water, but slowly the anxiety attack passed.
         </p>
<p id="Fragments_of_Fear_4M_topic_10__p__Hlk527011069">“I’m okay,” she whispered. “This will pass. Breathe in calm, breathe out fear. Om Mani Padme Hum.” She thought the chant sounded
            like a sick cow, but this week’s therapist wanted her to try it. The counselor before that gave her a journal to write in.
            And the string of psychiatrists her mother had set her up with after Grandma’s death all gave her drugs.
         </p>
<p id="Fragments_of_Fear_4M_topic_10__p__Hlk527016537">Once her heartbeat returned to normal, she stood. “It’s not my dog. Why should I care? I’ve never owned a dog. Dogs are great
            for books and movies, like <i>Turner and Hooch</i> or <i>Lassie</i>.” What if it was just a puppy? A tiny fur ball that just needed a home? A baby Old Yeller? Or Hachikō waiting by the train
            station? A lump closed up her throat and her eyes blurred. They wouldn’t kill a poor, defenseless puppy.
         </p>
<p><i>They will if I don’t save it.</i></p>
<p>She snatched the phone, then dialed her mother’s house. One of her mother’s gardeners or staff workers could retrieve the
            dog and find a place to keep it until she could locate the owner.
         </p>
<p>The housekeeper picked up. “Hi.” Tavish couldn’t remember this one’s name. She hadn’t set foot inside her mother’s house since
            her grandmother’s death more than a year ago, even though the front door was just a hundred yards away. “This is Tavish . . .
            um, Evelyn . . . Helen’s daughter. Is Maria there?”
         </p>
<p>“Maria?”</p>
<p>“Maria . . . um . . .” What was her last name? It started with an <i>M</i>. “The lady who cleans my house. She works for my mother.”
         </p>
<p>“No.”</p>
<p>“Okay then. I need you to send someone to the Happy Tails Shelter and retrieve a dog—”</p>
<p>“<i>Lo siento, pero no tengo coche, señorita.</i> No car.”
         </p>
<p>“The gardener . . . <i>el gardener </i>. . . ?”
         </p>
<p>“No. <i>Nadie está aquí todavía.</i>”
         </p>
<p>“Okay. Um, <i>gracias</i>.” Tavish disconnected, grabbed a handful of tissues and her purse and keys, then raced to her car. She punched the name of
            the shelter into her cell, then <i>directions</i>. That horrible man hadn’t said how long she had to fetch the poor dog. “You’d better not hurt that little puppy or . . .
            or I’ll . . .” What? Draw his picture? Stab him with a pencil? Throw crumpled-up newsprint at his head? Not a lot of vengeful
            acts available to her in her new career as a fine artist. Nor in her old career as an accountant. Hard to physically injure
            someone with numbers.
         </p>
<p>She could always just sit on him. The revenge of a fat lady.</p>
<p>She shot out of the driveway, almost sideswiping a passing pickup. The driver hit his horn.</p>
<p>“I’m so sorry!” <i>Should I stop and apologize?</i></p>
<p>The pickup turned the corner and drove off.</p>
<p>She bit a piece of cuticle off her thumb. Her horoscope said she’d have a surprise today. Who would have believed the surprise
            would be an unwanted puppy?
         </p>
<p>At the red light, she tugged on the crystal pendant around her neck. <i>Come on. Come on!</i> She’d get the canine thing straightened out. Maybe pay them to keep it for a few more days. Just long enough to get through
            her gallery opening. She’d place an ad in the newspaper. Find the owner . . . somehow. She glanced at her watch. The Albuquerque
            pet shelter was in the opposite direction from the gallery.
         </p>
<p class="txtb1">*  *  *</p>
<p>Cars and trucks parked unevenly in the packed dirt of the parking lot of Happy Tails Shelter. Tavish stepped into the dry,
            early-May sunshine and winced at the cacophony of wails and barking coming from the low pink-stuccoed building.
         </p>
<p>Inside the crowded lobby, a family of six hugged a wildly joyful yellow lab. The dog brushed against her, leaving fur on her
            tailored jeans. She brushed it off and avoided getting near two outdoorsy couples patting a shaggy mutt. In the corner, an
            elderly man held a small terrier. Three staff members wearing khaki shirts with <i>Happy Tails</i> and a dog’s rear end—apparently the logo—embroidered on the pocket held clipboards and were gesturing to the attached paper.
            Everyone was shouting over the din of barking.
         </p>
<p>A short man wearing the Happy Tails shirt made his way to her side. He sported a five-o’clock shadow, sunken cheeks, large
            nose, and receding chin. “Can I help you find a new fur baby?”
         </p>
<p><i>Fur baby?</i> She recognized the voice on the phone. “You just called me. The dog with the microchip—”
         </p>
<p>“Oh yeah.” His smile faded and eyes narrowed. “So you came to your senses and don’t want your dog killed.” He turned and strolled
            toward the kennel area, not waiting to see if she followed.
         </p>
<p>Tavish caught up with him. “I told you on the phone. It’s not my dog. I mean, dogs are fine in theory, and they make fine
            literature and films, but—”
         </p>
<p>He opened a door. The overwhelming sound of frantic barking and more people talking slammed into her ears. “I don’t have a
            dog.” She raised her voice over the din. “I don’t know why my name and address were connected to it.” The odor of disinfectant
            cleaners and urine burned her nose. “It must be a mistake.”
         </p>
<p>Chain-link dog runs lined either side of a hall, with several dogs of all sizes and breeds in each run. She made sure she
            walked down the middle. At the end of the hall, a door with a window marked No Admittance<i> </i>led to additional runs. He paused<i> </i>and pulled out a set of keys at the end of a chain. After unlocking the door, he passed through and stopped.
         </p>
<p>In the first run, a small black dog with a coat that looked like dreadlocks lay with its head between its paws, eyes watchful.</p>
<p>“Marley?” Tavish approached the kennel. “Marley?”</p>
<p>The Puli stood and stared at Tavish.</p>
<p>Tavish turned to the man. “I recognize the dog. But it’s not mine. As I told you, I don’t own a dog.”</p>
<p>Marley launched herself at the gate, leaping against it, barking wildly. The leaps turned to spins, then to a race from one
            end of the run to the other, long cords flying. She looked like an animated black string mop.
         </p>
<p>The man snorted. “I don’t care what you say. This is your dog, lady. Look at how she’s acting.”</p>
<p>The happy canine, now spinning in one place, continued to bark.</p>
<p id="Fragments_of_Fear_4M_topic_10__p__Hlk527011943">Tavish stepped back and brushed imaginary fur off her pressed jeans. “This is John Coyote’s dog.”</p>
<p>“Yeah, yeah, whatever. Can you show evidence of vaccination, including rabies?”</p>
<p>“How can I show you . . . ? Never mind.” After pulling out her phone, she thumbed through the numbers until she came to John’s.
            “I’ll call Marley’s owner.”
         </p>
<p>“You do that.”</p>
<p>The phone rang. And rang. And rang. “He’s not home.” Tavish disconnected. “How long can you keep her—”</p>
<p>“You don’t get it, do you, lady? Either you walk out of here with your dog, or she’s toast.” He leaned against the dog run.</p>
<p>Marley leaped away and lifted her lips, exposing sharp canines. The happy yips deepened to a bark worthy of a Doberman.</p>
<p>He straightened and jerked his thumb at the canine. “See what I mean? She’s vicious.”</p>
<p><i>Maybe she just has good sense. </i>“Not usually.”
         </p>
<p>The man’s expression didn’t change. “Look, ya either own a dog and take care of ’em, or ya put ’em up for adoption, or ya
            put ’em to sleep.” He leaned forward, his eyes narrowing to slits. “Ya don’t turn ’em loose where they’ll get hit by a car,
            or shot, or killed by other dogs, or chewed up by coyotes, or starve to death. Got it?”
         </p>
<p>She pulled at a hangnail on her left finger. “Mister . . . um . . . ?”</p>
<p>“Brown.”</p>
<p>“Look, Mr. Brown, I know this dog because I’m an artist and was commissioned to draw her and her owner. Well, actually paint
            them, but—”
         </p>
<p>Brown headed toward the lobby. “I got work to do. Either purchase her shots and get her license, or leave and we’ll take care
            of her.”
         </p>
<p>“Marley’s owner is John Coyote, a retired archaeologist.” She raced to keep up with him. Marley’s yelps grew frantic behind
            them. “He’s probably consulting on an archaeological dig somewhere, and Marley got loose. You just need to keep her—”
         </p>
<p>Brown stopped so abruptly that Tavish crashed into him. “Seventy-eight bucks. And you take her with you.”</p>
<p>She stepped back and sucked in air to keep a panic attack at bay. “As I can’t convince you of the truth, bring me whatever
            paperwork I need to sign and let me take Marley out of here.”
         </p>
<p>Brown gave a snarky grin. “I knew you’d finally admit to owning the mutt. And you might groom her occasionally.”</p>
<p>“She’s not a mutt. She’s small for her breed, but she’s a purebred Puli. They’re an ancient Hungarian sheepdog. That’s a corded
            coat. It’s not matted.”
         </p>
<p>Brown turned and headed to the lobby again. “Yeah, yeah.”</p>
<p>The front office was still chaotic with prospective owners and pets. Brown grabbed a clipboard and shoved it into her hands.
            “Fill this out and sign at the bottom. Eighty-nine dollars.”
         </p>
<p>“You said seventy-eight.”</p>
<p>“You heard wrong.”</p>
<p><i>He’s lying. Stand your ground. Confront him.</i> She looked down, resisted the urge to rip off a Band-Aid and bite a nail, and took the pen and clipboard. She didn’t have
            a choice. There was no way she’d convince Mr. Brown that Marley wasn’t her dog, and if she didn’t pay up, Marley would die.
            She filled out her own name and address, then pulled out a black American Express credit card.
         </p>
<p>Brown shook his head. “Cash. Or check.”</p>
<p>She returned the card and wrote a check for eighty-nine dollars. “The dog, please.” She had to wait another twelve minutes
            before Marley appeared.
         </p>
<p>“Promise me you’ll never let this dog end up like this again,” Mr. Brown said.</p>
<p>“I promise Marley will never see you or this dog prison again. And I keep my promises.”</p>
<p>Marley apparently forgot Tavish’s earlier abandonment and greeted her with excited, high-pitched barks. Tavish gingerly took
            the end of the cheap blue nylon slip lead and led her to the car. She hadn’t brought a towel or blanket for the canine to
            lie on. She opened the back door. Marley readily jumped in.
         </p>
<p>“Watch out for the artwork. They’re in that bag on the floor—”</p>
<p>Marley leaped to the front seat, leaving dusty footprints all over the immaculate interior. “You need to get into the back
            seat.”
         </p>
<p>The dog ignored her.</p>
<p>“Marley, I mean it.”</p>
<p>A woman passing the car stared at Tavish and frowned.</p>
<p>Tavish glanced around. Several people were watching her. She slipped into the driver’s seat and started the engine to engage
            the air-conditioning. “You were making a scene.”
         </p>
<p>The dog wagged her tail.</p>
<p>Tavish checked her watch, then dialed the gallery.</p>
<p id="Fragments_of_Fear_4M_topic_10__p__Hlk527012361">The clerk answered. “Hogarth and Montgomery Art Gallery, Lambert speaking.”</p>
<p>“Hi, Lambert, it’s Tavish. I’m running late—”</p>
<p>“I should say you are.” He sniffed. “Mrs. Hogarth will be <i>most</i> displeased. She wanted to hang those last two works an hour ago.”
         </p>
<p>Marley stuck her nose in Tavish’s hair, pawed at her hand, then licked her face just as Tavish opened her mouth. “Ack, akkk,
            ak!” She grabbed a tissue and scrubbed her tongue.
         </p>
<p>Lambert sniffed again. “Well, <i>you</i> may not care about your work, but we have a reputation—”
         </p>
<p>“She just stuck her tongue in my mouth.” Okay, that didn’t sound right. “I mean—”</p>
<p>“Miss McTavish, I don’t care about your personal life. I will tell Mrs. Hogarth you are delayed.”</p>
<p><i>One day I’m going to be famous for my art and I’ll take great delight in snubbing you</i>. <i>To your face</i>. Marley began to settle and curled up on the seat, nose jammed against the edge. “You’re a huge encumbrance in my life.”
         </p>
<p>The dog huffed once and started snoring.</p>
<p>“I saved your life, you know.”</p>
<p>Marley’s feet twitched as if she were chasing a cat.</p>
<p>“Lassie would have been grateful. Cujo would have been grateful.” She adjusted the air-conditioning vent to blow on the dog.
            “I suppose you didn’t get much sleep in the doggie slammer.”
         </p>
<p>Tavish reached behind her and made sure the framed drawings were still upright on the floor. She tugged on her crystal. “So,
            Marley, here’s my dilemma.”
         </p>
<p><i>You’re talking to a dog.</i></p>
<p>She cleared her throat. “I’m sure John’s talked to you a lot, right?”</p>
<p>Marley opened one eye.</p>
<p>“I thought so. I have to deliver those two framed works, but Mrs. Hogarth would have a meltdown if I brought a dog into her
            gallery.”
         </p>
<p>Marley closed her eyes, shifted, and snuggled deeper in the seat.</p>
<p>“Right. I’ll try John again.” She dialed. The phone rang ten times before she hung up and started the car. “I agree. His answering
            machine should have picked up.” She rubbed the slight tingling between her shoulder blades. “John must be beside himself looking
            for you . . . or . . . maybe he’s out of town and a dog sitter accidentally let you loose.”
         </p>
<p>But he’d never mentioned what he did with his dog when he traveled.</p>
<p>She gave another tug on her crystal. “Well, my dreadlocked friend, since I’m already late, I might as well be even later and
            drop you off first. Even though, of course, John’s home is way out of town.”
         </p>
<p>She stared at the sleeping dog for a moment. “You didn’t by any chance pick up fleas, did you?”</p>
<p>Marley sat up, barked once, then stared at her.</p>
<p>“Okay, no offense meant.” She put the car into gear and pulled out into the street. “So here’s the deal. My life is . . .
            complicated right now. I don’t have time to locate your owner, but you have a doggie door in the backyard and I’ll make sure
            you have water. If I don’t see John today, I can return and check on you tomorrow.” She turned toward Interstate 40 and the
            Tijeras Canyon, heading east. “It’s not that I don’t like you or anything. It’s just . . . I’ve never owned a dog. Don’t get
            me wrong. I love books and movies about canines. It’s just . . . well, at your home, you’ll have dog stuff . . . like food,
            and toys, and . . . stuff. And besides, I’m not really very good company.” She glanced at the Band-Aids on her fingers.
         </p>
<p>Marley rolled onto her back.</p>
<p>“Really, Marley, that’s a most immodest angle. Anyway, John can settle up the eighty-nine-dollar bail. And I can ask him why
            he listed my name and address on the microchip embedded in you, especially since I hardly know the man. I mean, he was just
            a client, a patron.”
         </p>
<p>Marley snorted in her sleep.</p>
<p>“Right. I forgot you were present when I came over.”</p>
<p>Although she’d only been to John’s home once, it was recently, and she remembered the way. John’s home was tucked into the
            mountains, surrounded by ponderosa pines and sagebrush. After turning off the interstate, she tracked down a narrow road until
            she reached the end of his drive, passing few houses and even fewer signs of life. It was difficult to see his house through
            the planted trees and shrubs, and the wrought-iron gate ensured his privacy. She tugged her cell out of her jeans to try his
            number one last time, but she didn’t have cell service.
         </p>
<p id="Fragments_of_Fear_4M_topic_10__p__Hlk527012977">Rolling down the window, she mentally reviewed his passcode. One-zero-six-three-four-zero-three-three-two. She was good at
            remembering numbers. She hoped he hadn’t changed the code. The fresh scent of sage drifted through the open window. Marley
            sat up, looked around, and barked.
         </p>
<p>The gate was already open. The narrow paved driveway curved around to a separate garage a fair distance from the house, with
            a path leading to the Pueblo Revival–style home. She drove slightly past the house, then turned around and parked under a
            shade tree. Exiting the car, she slipped her cell into a rear pocket. Taking Marley’s lead, she unloaded the dog, then paced
            to the garage and glanced through the window. John’s late-model Toyota was parked in its usual spot.
         </p>
<p>High overhead, a pair of large black birds gave a grunting cry as they circled. Wind rustled through the pines and sent the
            dry grasses hissing. The <i>clack-clack-clack </i>of her heels on the pavers sounded abnormally loud as she approached the entrance. Marley pranced ahead, pulling at the leash.
         </p>
<p>The front door stood open several inches.</p>
<p>“John? Mr. Coyote? It’s me, Tavish.”</p>
<p>The wind answered with a chindi, a dust devil on the hillside.</p>
<p>“Mr. Coyote?” She nudged the door open.</p>
<p>John Coyote lay sprawled just inside the door on the hand-painted tile floor. Round burn marks the diameter of a cigarette
            dotted his face, and red bruises circled his wrists. His shirt was stained maroon, a knife extended from his chest, and a
            puddle of coppery-smelling blood formed a moat around his body.
         </p>
<p>His eyes stared upward at eternity.</p>
</section>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Chapter_2.vue?5845e81b